<template>
  <hypto-popup title="Transfer Funds">
    <template v-slot:body>
      <div class="merge-fields">
        <div class="connected-banking" v-if="isDashboard && manageAccess.connectedBankingPayout.icici">
          <md-checkbox v-model="connectedBanking" />
          <p>Connected Banking</p>
          <i class="fa fa-info-circle">
            <md-tooltip md-direction="top">Checking this box will initiate a transfer from your linked ICICI Bank account.</md-tooltip>
          </i>
        </div>
        <error-info :errorList="formErrors.connected_banking" />

        <md-field :class="{'input-error': $v.amount.$error || transferLimitError || amountLimitCrossed || formErrors.amount.length}">
          <label>Amount</label>
          <md-input v-model="amount" @blur="$v.amount.$touch()"
                    :type="isDesktop() ? 'text' : 'number'" :ref="'Amount'"
                    @keypress="allowAmountFormat($event, amount)" />
        </md-field>
        <i class="amount">{{words}}</i>
        <h6 class="error-message" v-if="$v.amount.$error && !$v.amount.amount">Please enter only numeric values (upto 2 decimals)</h6>
        <h6 class="error-message" v-else-if="$v.amount.$error && (!$v.amount.required || !$v.amount.minValue)">Minimum amount value should be &#8377; 1</h6>
        <h6 class="error-message" v-else-if="amountLimitCrossed">Amount value should be less than current balance</h6>
        <h6 class="error-message" v-else-if="transferLimitError">{{transferLimitError}}</h6>
        <error-info v-else :errorList="formErrors.amount" />

        <md-field id="PaymentType" :class="{'input-error': formErrors.payment_type.length}">
          <label>Transfer Type</label>
          <md-select v-model="paymentType" @md-opened="setDropdownPosOffset('PaymentType')">
            <md-option v-for="(type, index) in paymentOptions" :key="index" :value="type">
              {{type}}
            </md-option>
          </md-select>
        </md-field>
        <error-info :errorList="formErrors.payment_type" />

        <md-field :class="{'input-error': $v.accountNumber.$error || formErrors.number.length}">
          <label>Account Number</label>
          <md-input v-model="accountNumber" @blur="$v.accountNumber.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.accountNumber.$error && !$v.accountNumber.required">Please enter Account Number</h6>
        <h6 class="error-message" v-else-if="$v.accountNumber.$error && !$v.accountNumber.accountNumber">Account Number should contain 1 to 34 alphanumeric characters</h6>
        <error-info v-else :errorList="formErrors.number" />

        <md-field :class="{'input-error': $v.ifsc.$error || formErrors.ifsc.length}">
          <label>IFSC</label>
          <md-input v-model="ifsc" @blur="$v.ifsc.$touch()" maxlength="11"
                    class="upper-case" />
        </md-field>
        <h6 class="error-message" v-if="$v.ifsc.$error && !$v.ifsc.required">Please enter IFSC Number</h6>
        <h6 class="error-message" v-else-if="$v.ifsc.$error && !$v.ifsc.validIfsc">Invalid IFSC Number. Please verify</h6>
        <error-info v-else :errorList="formErrors.ifsc" />
        <ifsc-details :bankDetails="bankDetails" />

        <md-field :class="{'input-error': $v.beneficiaryName.$error || formErrors.beneficiary_name.length}">
          <label>Beneficiary Name</label>
          <md-input v-model="beneficiaryName" @blur="$v.beneficiaryName.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.beneficiaryName.$error && !$v.beneficiaryName.required">Please enter Beneficiary Name</h6>
        <h6 class="error-message" v-else-if="$v.beneficiaryName.$error && !$v.beneficiaryName.alphaNumericSpace">Beneficiary Name should contain only alphanumeric characters and space</h6>
        <h6 class="error-message" v-else-if="$v.beneficiaryName.$error && !$v.beneficiaryName.maxLength">Beneficiary Name should contain maximum 35 characters</h6>
        <error-info v-else :errorList="formErrors.beneficiary_name" />

        <md-field v-if="paymentType === 'UPI'" :class="{'input-error': $v.upiId.$error || ($v.upiId.$dirty && invalidConditionalUpi) || formErrors.upi_id.length || trackUpi.invalid}">
          <label>UPI ID</label>
          <md-input v-model="upiId" @blur="touchUpi" />
        </md-field>
        <h6 class="error-message" v-if="$v.upiId.$dirty && invalidConditionalUpi">Please provide either upi_id or account number &amp; ifsc for UPI transfer</h6>
        <h6 class="error-message" v-else-if="$v.upiId.$error">Invalid UPI ID</h6>
        <h6 class="error-message" v-else-if="trackUpi.invalid">IFSC present in UPI ID is invalid</h6>
        <error-info v-else :errorList="formErrors.upi_id" />

        <md-field :class="{'input-error': $v.note.$error || formErrors.note.length}">
          <label>Note</label>
          <md-input v-model="note" @blur="$v.note.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.note.$error && !$v.note.required">Please enter a Note</h6>
        <h6 class="error-message" v-else-if="$v.note.$error && !$v.note.minLength">Note should contain atleast 2 characters</h6>
        <h6 class="error-message" v-else-if="$v.note.$error && !$v.note.alphaNumericSpace">Note should contain only alphanumeric characters and space</h6>
        <error-info v-else :errorList="formErrors.note" />

        <md-field :class="{'input-error': $v.refNum.$error || formErrors.reference_number.length}">
          <label>Reference Number</label>&nbsp;
          <i>(Optional)</i>
          <md-input v-model="refNum" @keyup.enter="transfer" @blur="$v.refNum.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.refNum.$error">Reference Number should contain only alphanumeric characters, hyphen, underscore or dot</h6>
        <error-info v-else :errorList="formErrors.reference_number" />
      </div>
    </template>

    <template v-slot:footer>
      <error-info :errorList="formErrors.transaction" />
      <button class="primary-btn align-r" @click="transfer" :disabled="isDisableButton">
        Initiate Transfer
      </button>
    </template>
  </hypto-popup>
</template>

<script>
import HyptoPopup from '@/components/global/HyptoPopup'
import ErrorInfo from '@/components/global/ErrorInfo'
import IfscDetails from '@/components/AccountSummary/IfscDetails'

import { required, minLength, maxLength, minValue, requiredIf } from 'vuelidate/lib/validators'
import { amount, alphaNumericSpace, validIfsc, validVpa, accountNumber, refNum } from '@/utils/helpers/validators'
import { mapMutations, mapGetters } from 'vuex'
import { setDropdownPosOffset, checkFormErrors, allowAmountFormat, amountFormat } from '@/utils/helpers'
import ApiServices from '@/services/api/transfers'
import formMixins from '@/mixins/formMixins'
import numToWords from '@/mixins/numToWords'
import moment from 'moment'

export default {
  name: 'BankTransfer',
  components: { HyptoPopup, ErrorInfo, IfscDetails },
  mixins: [formMixins, numToWords],
  data () {
    return {
      connectedBanking: false,
      paymentType: 'IMPS',
      paymentOptions: [
        'IMPS', 'RTGS', 'NEFT', 'UPI'
      ],
      beneficiaryName: null,
      ifsc: null,
      accountNumber: null,
      upiId: null,
      note: null,
      refNum: null,
      neft: null,
      rtgs: null,
      formErrors: {
        amount: [],
        payment_type: [],
        ifsc: [],
        number: [],
        upi_id: [],
        beneficiary_name: [],
        note: [],
        reference_number: [],
        connected_banking: [],
        transaction: []
      },
      bankDetails: {},
      trackUpi: {
        invalid: false,
        changed: false
      },
      inProgress: false
    }
  },
  props: {
    manageAccess: Object,
    vaId: {
      type: [Number, Boolean],
      default: false
    },
    subVaBalance: {
      type: Number,
      default: 0
    }
  },
  validations: {
    amount: {
      required,
      amount,
      minValue: minValue(1)
    },
    beneficiaryName: {
      required,
      alphaNumericSpace,
      maxLength: maxLength(35)
    },
    ifsc: {
      required: requiredIf('notUpiPayment'),
      validIfsc
    },
    accountNumber: {
      required: requiredIf('notUpiPayment'),
      accountNumber
    },
    upiId: {
      validVpa
    },
    note: {
      required,
      alphaNumericSpace,
      minLength: minLength(2)
    },
    refNum: {
      refNum
    }
  },
  created () {
    this.refetchBalance()
  },
  mounted () {
    this.$refs.Amount.$el.focus()
  },
  computed: {
    ...mapGetters(['getterAccountBalance']),
    invalidConditionalUpi () {
      let error = false
      if (this.paymentType === 'UPI') {
        if ((this.accountNumber || this.ifsc) && this.upiId) {
          error = true
        } else if ((this.accountNumber && this.ifsc) || this.upiId) {
          error = false
        } else {
          error = true
        }
      }
      return error
    },
    transferLimitError () {
      const verifyLimit = (transfer) => {
        if (this[transfer].banking_hours) {
          const startTime = this[transfer].banking_hours.from
          const endTime = this[transfer].banking_hours.to
          if (moment().isBetween(startTime, endTime)) {
            if (this.amount > this[transfer].bh_limit) {
              return `Please enter maximum amount of ${this.amountFormat(this[transfer].bh_limit)} for ${transfer.toUpperCase()} transfers, during banking hours (${moment(startTime).format('HH:mm:ss')} - ${moment(endTime).format('HH:mm:ss')})`
            }
          } else if (this[transfer].nbh_limit && this.amount > this[transfer].nbh_limit) {
            return `Please enter maximum amount of ${this.amountFormat(this[transfer].nbh_limit)} for ${transfer.toUpperCase()} transfers, during non-banking hours`
          }
        } else if (this[transfer].nbh_limit && this.amount > this[transfer].nbh_limit) {
          return `Please enter maximum amount of ${this.amountFormat(this[transfer].nbh_limit)} for ${transfer.toUpperCase()} transfers, during bank holidays`
        }
      }

      if (this.paymentType === 'NEFT' && this.neft) {
        return verifyLimit('neft')
      } else if (this.paymentType === 'RTGS') {
        if (this.amount < 200000) {
          return 'Please enter minimum amount of Rs.2 lakh for RTGS transfers'
        } else if (this.rtgs) {
          return verifyLimit('rtgs')
        }
      } else if (this.paymentType === 'IMPS' && this.amount > 500000) {
        return 'Please enter maximum amount of Rs.5 lakh for IMPS transfers'
      } else if (this.paymentType === 'UPI' && this.amount > 100000) {
        return 'Please enter maximum amount of Rs.1 lakh for UPI transfers'
      }
      return false
    },
    notUpiPayment () {
      return this.paymentType !== 'UPI'
    },
    isDashboard () {
      return !this.vaId
    },
    amountLimitCrossed () {
      if (this.isDashboard) {
        return this.manageAccess.accountBalance && this.amount > this.getterAccountBalance
      } else {
        return this.manageAccess.vaBalance && this.amount > this.subVaBalance
      }
    }
  },
  watch: {
    ifsc () {
      this.bankDetails = {}

      if (this.ifsc.length === 11 && validIfsc(this.ifsc)) {
        this.ifscDetails()
      }
      this.formErrors.transaction = []
      this.formErrors.ifsc = []
    },
    amount () {
      this.formErrors.transaction = []
      this.formErrors.amount = []
      this.convertWords()
    },
    refNum () {
      this.formErrors.transaction = []
      this.formErrors.reference_number = []
    },
    paymentType (newVal, oldVal) {
      if (oldVal === 'UPI') {
        this.upiId = null
        this.$v.upiId.$reset()
      }
      this.formErrors.transaction = []
      this.formErrors.payment_type = []
    },
    beneficiaryName () {
      this.formErrors.transaction = []
      this.formErrors.beneficiary_name = []
    },
    accountNumber () {
      this.formErrors.transaction = []
      this.formErrors.number = []
    },
    note () {
      this.formErrors.transaction = []
      this.formErrors.note = []
    },
    upiId () {
      this.trackUpi = {
        invalid: false,
        changed: true
      }
      this.formErrors.transaction = []
      this.formErrors.upi_id = []
    },
    connectedBanking () {
      this.formErrors.transaction = []
      this.formErrors.connected_banking = []
    }
  },
  methods: {
    ...mapMutations(['SET_ACCOUNT_BALANCE', 'SET_POPUP_NAME', 'SET_BANK_TRANSFERRED']),
    setDropdownPosOffset,
    checkFormErrors,
    allowAmountFormat,
    amountFormat,
    getParams () {
      let params = {
        connected_banking: this.connectedBanking,
        amount: parseFloat(this.amount),
        payment_type: this.paymentType,
        note: this.note,
        beneficiary_name: this.beneficiaryName
      }
      if (this.accountNumber) {
        params['number'] = this.accountNumber
      }
      if (this.ifsc) {
        params['ifsc'] = this.ifsc.toUpperCase()
      }
      if (this.upiId) {
        params['upi_id'] = this.upiId
      }
      if (this.refNum) {
        params['reference_number'] = this.refNum
      }

      if (!this.isDashboard) {
        params['hypto_va_id'] = this.vaId
      }
      return params
    },
    transfer () {
      this.$v.$touch()
      if (!this.$v.$error && !this.invalidConditionalUpi && !this.transferLimitError && !this.checkFormErrors(this.formErrors) && !this.inProgress && !this.trackUpi.invalid && !this.amountLimitCrossed) {
        this.inProgress = true
        const params = this.getParams()

        ApiServices.initiateTransfer(params).then((response) => {
          if (response.success) {
            this.successToastMsg(response.message)
            this.SET_POPUP_NAME(null)

            if (this.isDashboard) {
              this.SET_ACCOUNT_BALANCE(response.data.closing_balance)
              this.SET_BANK_TRANSFERRED()
            } else {
              this.setSubVaBalance({va_balance: response.data.closing_balance}, true)
            }
          } else {
            this.errorToastMsg(response.message)
          }

          this.enableButton()
        }).catch((error) => {
          let data = error.body
          this.formErrors = {
            ...this.formErrors,
            ...data.reason
          }
          this.inProgress = false
        })
      }
    },
    refetchBalance () {
      let params = {}
      if (this.vaId) {
        params['hypto_va_id'] = this.vaId
      }

      ApiServices.transferDetails(params).then((response) => {
        const data = response.data

        if (data) {
          const limits = data.limits
          this.neft = limits['NEFT']
          this.rtgs = limits['RTGS']

          if (this.isDashboard) {
            this.setDashboardBalance(data)
          } else {
            this.setSubVaBalance(data)
          }
        }
      })
    },
    setDashboardBalance (data) {
      if (data.hasOwnProperty('balance')) {
        this.$emit('balanceAccess', true)
        this.SET_ACCOUNT_BALANCE(data.balance)
      } else {
        this.$emit('balanceAccess', false)
      }
    },
    setSubVaBalance (data, transferInitiated = false) {
      if (data.hasOwnProperty('va_balance')) {
        this.$emit('updateSubVaBalance', {access: true, balance: data.va_balance}, transferInitiated)
      } else {
        this.$emit('updateSubVaBalance', {access: false}, transferInitiated)
      }
    },
    ifscDetails () {
      if (!this.$v.ifsc.$error) {
        ApiServices.ifscDetails(this.ifsc).then(response => {
          this.bankDetails = response.data
        }).catch(error => {
          this.formErrors.ifsc = [error.body.message]
        })
      }
    },
    touchUpi () {
      this.$v.upiId.$touch()
      if (!this.$v.upiId.$error && this.upiId && this.upiId.includes('.ifsc') && this.trackUpi.changed) {
        const pos1 = this.upiId.indexOf('@') + 1
        const pos2 = this.upiId.indexOf('.ifsc')
        const ifsc = this.upiId.substring(pos1, pos2)

        ApiServices.ifscDetails(ifsc).then(response => {
          this.trackUpi = {
            invalid: false,
            changed: false
          }
        }).catch(() => {
          this.trackUpi = {
            invalid: true,
            changed: false
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/mixins.scss';

  .connected-banking {
    @include custom-flex('');

    p {
      @include normal-text($secondary, 14px, 500);
      margin-right: 10px;
    }
    .fa-info-circle {
      cursor: pointer;
      color: $primary;
    }
  }

  .upper-case {
    text-transform: uppercase;
  }
  i {
    color: $grey-light;

    &.amount {
      display: block;
      text-align: left;
      font-size: 12px;
    }
  }

  .ifsc-details {
    @include custom-flex(null, flex-end, column);

    .row {
      display: flex;
      font-size: 13px;

      b {
        margin-right: 5px;
        color: $secondary;
        font-weight: 600;
        white-space: nowrap;
      }
      span {
        margin-left: 5px;
        color: $primary;
        font-weight: 500;
        text-align: right;
      }
    }
  }
</style>
